import { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  MenuItem,
  Alert,
  Grid,
  Divider,
  InputAdornment,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient, useIsFetching } from '@tanstack/react-query'
import {
  scoreService,
  accountService,
  driverService,
  vehicleService,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { useToast } from '../components/useToast'
import PageRefreshButton from '../components/PageRefreshButton'

type EntityType = 'DRIVER' | 'VEHICLE' | 'FREIGHTER'

export default function ScoresPage() {
  const [entityType, setEntityType] = useState<EntityType>('DRIVER')
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const [entityId, setEntityId] = useState('')
  const [adjustment, setAdjustment] = useState('')
  const [reason, setReason] = useState('')
  const toast = useToast()
  const getLocalizedLabel = useLocalizedLabel()
  const queryClient = useQueryClient()

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-scores', 0, 200],
    queryFn: () => accountService.getAll(0, 200),
  })

  const accountsList: any[] = accountsData?.content || accountsData || []
  // Drivers/vehicles belong to FREIGHTER accounts; FREIGHTER entity is the account itself.
  const freighterAccounts = accountsList.filter(
    (a: any) => String(a.type || '').toUpperCase() === 'FREIGHTER'
  )

  const { data: driversData } = useQuery({
    queryKey: ['drivers-for-scores', selectedAccountId],
    queryFn: () => driverService.getAllByAccount(selectedAccountId),
    enabled: !!selectedAccountId && entityType === 'DRIVER',
  })
  const drivers: any[] = driversData || []

  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles-for-scores', selectedAccountId],
    queryFn: () => vehicleService.getAllByAccount(selectedAccountId),
    enabled: !!selectedAccountId && entityType === 'VEHICLE',
  })
  const vehicles: any[] = vehiclesData || []

  // Current score of the picked entity (for the preview panel).
  const { data: currentScoreData, isLoading: scoreLoading } = useQuery({
    queryKey: ['current-score', entityType, entityId],
    queryFn: () => scoreService.getScore(entityType, entityId),
    enabled: !!entityId,
    retry: 0,
  })
  const currentScore: number | null = useMemo(() => {
    if (!currentScoreData) return null
    const v: any = currentScoreData
    const n =
      typeof v === 'number'
        ? v
        : typeof v.score === 'number'
        ? v.score
        : typeof v.value === 'number'
        ? v.value
        : null
    return n
  }, [currentScoreData])

  const adjustmentInt = parseInt(adjustment, 10)
  const projectedScore =
    currentScore != null && Number.isFinite(adjustmentInt)
      ? Math.max(0, Math.min(1000, currentScore + adjustmentInt))
      : null
  const projectedClamped =
    currentScore != null &&
    Number.isFinite(adjustmentInt) &&
    currentScore + adjustmentInt !== projectedScore

  const handleEntityTypeChange = (type: EntityType) => {
    setEntityType(type)
    setSelectedAccountId('')
    setEntityId('')
  }

  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId)
    if (entityType === 'FREIGHTER') {
      setEntityId(accountId)
    } else {
      setEntityId('')
    }
  }

  const invalidateScore = () =>
    queryClient.invalidateQueries({ queryKey: ['current-score', entityType, entityId] })

  const adjustMutation = useMutation({
    mutationFn: ({ entityType, entityId, request }: any) =>
      scoreService.adjustScore(entityType, entityId, request),
    onSuccess: () => {
      toast.success('Score adjusted successfully')
      setAdjustment('')
      setReason('')
      invalidateScore()
    },
    onError: (error) => toast.errorFrom(error, 'Failed to adjust score'),
  })

  const resetMutation = useMutation({
    mutationFn: ({ entityType, entityId, reason }: any) =>
      scoreService.resetScore(entityType, entityId, reason),
    onSuccess: () => {
      toast.success('Score reset to baseline (500)')
      setReason('')
      invalidateScore()
    },
    onError: (error) => toast.errorFrom(error, 'Failed to reset score'),
  })

  const handleAdjust = () => {
    if (!entityId) return toast.info('Please select or enter an entity')
    if (!adjustment) return toast.info('Please enter an adjustment value')
    if (!reason) return toast.info('Please provide a reason')
    adjustMutation.mutate({
      entityType,
      entityId,
      request: { adjustment: parseInt(adjustment), reason },
    })
  }

  const handleReset = () => {
    if (!entityId) return toast.info('Please select or enter an entity')
    if (!reason) return toast.info('Please provide a reason')
    resetMutation.mutate({ entityType, entityId, reason })
  }

  const isPending = adjustMutation.isPending || resetMutation.isPending

  const isScoresRefreshing = useIsFetching({
    predicate: (query) => {
      const key = query.queryKey[0]
      return (
        key === 'accounts-for-scores' ||
        key === 'drivers-for-scores' ||
        key === 'vehicles-for-scores' ||
        key === 'current-score'
      )
    },
  }) > 0

  const refreshScoresData = () => {
    queryClient.invalidateQueries({ queryKey: ['accounts-for-scores'] })
    if (selectedAccountId) {
      queryClient.invalidateQueries({ queryKey: ['drivers-for-scores', selectedAccountId] })
      queryClient.invalidateQueries({ queryKey: ['vehicles-for-scores', selectedAccountId] })
    }
    if (entityId) {
      queryClient.invalidateQueries({ queryKey: ['current-score', entityType, entityId] })
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <PageRefreshButton onRefresh={refreshScoresData} loading={isScoresRefreshing} />
      </Stack>
      <Grid container spacing={3}>
        {/* Entity Selection Panel */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              1. Select Entity
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Entity Type"
                value={entityType}
                onChange={(e) => handleEntityTypeChange(e.target.value as EntityType)}
                fullWidth
              >
                <MenuItem value="DRIVER">Driver</MenuItem>
                <MenuItem value="VEHICLE">Vehicle</MenuItem>
                <MenuItem value="FREIGHTER">
                  {getLocalizedLabel('accountType', 'FREIGHTER')}
                </MenuItem>
              </TextField>

              <TextField
                select
                label={
                  entityType === 'FREIGHTER'
                    ? 'Freighter Account'
                    : `Freighter Account (owner of the ${entityType.toLowerCase()})`
                }
                value={selectedAccountId}
                onChange={(e) => handleAccountChange(e.target.value)}
                fullWidth
                helperText={
                  entityType === 'FREIGHTER'
                    ? "This account's ID will be used as the entity"
                    : `Select the account that owns the ${entityType.toLowerCase()}`
                }
              >
                <MenuItem value="">-- Select Account --</MenuItem>
                {freighterAccounts.map((account: any) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.businessName || account.name}
                    {account.organizationCode ? ` (${account.organizationCode})` : ''}
                  </MenuItem>
                ))}
              </TextField>

              {entityType === 'DRIVER' && selectedAccountId && (
                <TextField
                  select
                  label="Driver"
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  fullWidth
                  helperText={drivers.length === 0 ? 'No drivers found for this account' : undefined}
                >
                  <MenuItem value="">-- Select Driver --</MenuItem>
                  {drivers.map((driver: any) => (
                    <MenuItem key={driver.id} value={driver.id}>
                      {driver.user?.name || driver.licenseNo || driver.id.substring(0, 8)}
                      {driver.user?.status ? ` — ${driver.user.status}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {entityType === 'VEHICLE' && selectedAccountId && (
                <TextField
                  select
                  label="Vehicle"
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  fullWidth
                  helperText={vehicles.length === 0 ? 'No vehicles found for this account' : undefined}
                >
                  <MenuItem value="">-- Select Vehicle --</MenuItem>
                  {vehicles.map((v: any) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.plateNo}
                      {v.maker || v.model ? ` — ${[v.maker, v.model].filter(Boolean).join(' ')}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <Divider>
                <Typography variant="caption" color="text.secondary">
                  OR enter UUID directly
                </Typography>
              </Divider>

              <TextField
                label="Entity ID (UUID)"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                fullWidth
                size="small"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                }}
              />

              {entityId && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {entityType}: {entityId}
                  </Typography>
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Score Action Panel */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              2. Score Action
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info">
                Baseline score is <strong>500</strong>. Valid range: 0 – 1000. Adjustments must
                include a reason for the audit log.
              </Alert>

              {/* Current score + projection */}
              {entityId && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current score
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {scoreLoading
                          ? '…'
                          : currentScore != null
                          ? currentScore
                          : 'unknown'}
                      </Typography>
                    </Box>
                    {projectedScore != null && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary">
                          After adjustment
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="flex-end">
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            color={
                              projectedScore > (currentScore ?? 0)
                                ? 'success.main'
                                : projectedScore < (currentScore ?? 0)
                                ? 'warning.main'
                                : 'text.primary'
                            }
                          >
                            {projectedScore}
                          </Typography>
                          {projectedClamped && (
                            <Chip
                              size="small"
                              color="warning"
                              label="clamped to 0–1000"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              )}

              <TextField
                label="Score Adjustment (points)"
                type="number"
                value={adjustment}
                onChange={(e) => setAdjustment(e.target.value)}
                fullWidth
                inputProps={{ min: -500, max: 500 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {adjustment && parseInt(adjustment) > 0 ? '+' : ''}
                    </InputAdornment>
                  ),
                }}
                helperText="Positive value increases score; negative value decreases it"
                disabled={isPending}
              />

              <TextField
                label="Reason *"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
                placeholder="Explain why you are adjusting this score (required for audit log)..."
                disabled={isPending}
              />

              {(adjustMutation.isError || resetMutation.isError) && (
                <Alert severity="error">
                  {(adjustMutation.error as any)?.response?.data?.message ||
                    (resetMutation.error as any)?.response?.data?.message ||
                    'An error occurred'}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    onClick={handleAdjust}
                    disabled={isPending || !entityId || !adjustment || !reason}
                    fullWidth
                    size="large"
                  >
                    Apply Adjustment
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleReset}
                    disabled={isPending || !entityId || !reason}
                    fullWidth
                    size="large"
                  >
                    Reset to 500
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}
